import { Controller } from "@hotwired/stimulus"
import { formatMoney } from "./utils/money"

// purchase-preview — preview vivo do form de compra: espelha descrição, valor
// total e o desdobramento em parcelas ("12x de R$ 300,00", calculado ao vivo do
// total ÷ parcelas), além do ícone/nome da categoria escolhida. O chip clona o
// SVG da categoria de uma grade oculta renderizada server-side (CSP-safe).
//
// Uso: data-controller="purchase-preview" com:
//   inputs: descriptionInput, totalInput, installmentsInput, categoryInput (<select>)
//   saída:  chip, descOut, subOut, amountOut
//   grade:  iconCell (uma por categoria; data-category-id, contém o SVG)
//   values: descFallback, currencySymbol, atSightLabel, perInstallmentTemplate
export default class extends Controller {
  static targets = [
    "descriptionInput", "totalInput", "installmentsInput", "categoryInput",
    "chip", "descOut", "subOut", "amountOut", "iconCell"
  ]
  static values = {
    descFallback:   String,  // ex.: "Descreva a compra…"
    currencySymbol: String,  // ex.: "R$"
    currency: Object,        // meta da moeda (FormatHelper::CURRENCY_META)
    atSightLabel:   String,  // ex.: "à vista"
    perInstallmentTemplate: String // ex.: "%{n}x de %{amount}"
  }

  connect() {
    this.render()
    this._renderIcon()
  }

  render() {
    // Descrição.
    if (this.hasDescOutTarget) {
      const v = this.hasDescriptionInputTarget ? this.descriptionInputTarget.value.trim() : ""
      this.descOutTarget.textContent = v || this.descFallbackValue || ""
    }
    // Valor total.
    const cents = this._totalCents()
    if (this.hasAmountOutTarget) {
      this.amountOutTarget.textContent = cents > 0 ? this._formatMoney(cents) : this._formatMoney(0)
    }
    // Sub: categoria · (à vista | Nx de R$ Y).
    if (this.hasSubOutTarget) {
      const parts = []
      const cat = this._categoryName()
      if (cat) parts.push(cat)
      parts.push(this._installmentLabel(cents))
      this.subOutTarget.textContent = parts.join(" · ")
    }
  }

  changeCategory() {
    this.render()
    this._renderIcon()
  }

  // ---- Parcelamento ----
  _installmentLabel(totalCents) {
    const n = this._installments()
    if (n <= 1) return this.atSightLabelValue || ""
    const per = Math.floor(totalCents / n)
    return (this.perInstallmentTemplateValue || "%{n}x de %{amount}")
      .replace("%{n}", String(n))
      .replace("%{amount}", this._formatMoney(per))
  }

  _installments() {
    if (!this.hasInstallmentsInputTarget) return 1
    const n = parseInt(this.installmentsInputTarget.value, 10)
    return Number.isFinite(n) && n > 0 ? n : 1
  }

  // ---- Valor ----
  _totalCents() {
    if (!this.hasTotalInputTarget) return 0
    const digits = (this.totalInputTarget.value || "").replace(/\D/g, "")
    return digits ? parseInt(digits, 10) : 0
  }

  // Formata pelo meta da moeda do app (mesmo algoritmo do servidor - utils/money).
  // Antes fixava pt-BR e 2 casas aqui: em JPY/CLP inventava decimais que o servidor
  // não renderiza, e os separadores saíam trocados fora de BRL.
  _formatMoney(cents) {
    return formatMoney(cents, this.hasCurrencyValue ? this.currencyValue : { symbol: this.currencySymbolValue })
  }

  // ---- Categoria ----
  _categoryName() {
    if (!this.hasCategoryInputTarget) return ""
    const sel = this.categoryInputTarget
    const opt = sel.options[sel.selectedIndex]
    return sel.value && opt ? opt.textContent.trim() : ""
  }

  _renderIcon() {
    if (!this.hasChipTarget) return
    const id = this.hasCategoryInputTarget ? this.categoryInputTarget.value : ""
    const cell = this.iconCellTargets.find((c) => c.dataset.categoryId === String(id)) ||
                 this.iconCellTargets[0]
    const svg = cell && cell.querySelector("svg")
    if (svg) this.chipTarget.innerHTML = svg.outerHTML
  }
}
